<script lang="ts" setup>
useHead({
  link: [
    {
      rel: 'icon',
      type: 'image/svg',
      href: '/favicon.svg',
      media: '(prefers-color-scheme: light)',
    },
    {
      rel: 'icon',
      type: 'image/svg',
      href: '/favicon-light.svg',
      media: '(prefers-color-scheme: dark)',
    },
    {
      rel: 'stylesheet',
      href: 'https://use.typekit.net/hgw7mzy.css',
    },
  ],
})

const onBeforeLeave = () => {
  // Save current scroll position
  const scrollY = window.scrollY
  document.body.style.position = 'fixed'
  document.body.style.width = '100%'
  document.body.style.top = `-${scrollY}px`
}

const onEnter = (el: Element, done: () => void) => {
  // Remove fixed positioning
  const body = document.body
  body.style.position = ''
  body.style.width = ''
  body.style.top = ''

  // Ensure the element starts in its initial state
  el.style.opacity = '0'
  el.style.filter = 'blur(1rem)'

  // Force a reflow
  el.offsetHeight

  // Clear the inline styles to let CSS transitions take over
  el.style.opacity = ''
  el.style.filter = ''

  // Scroll to top after transition
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
    done()
  }, 400)
}

const { data: colorThemes } = await useSanityQuery(getColorThemes)
const themeSettings = useState('themeSettings', () => colorThemes?.value || [])

document?.body?.classList.add(...['overflow-x-clip', 'font-sans'])

onMounted(() => {
  useSetVariables(themeSettings?.value)
})
const localePath = useLocalePath()
const { locale } = useI18n()

const globalDataEndpoints = ['footer', 'navigation', 'settings']
const { globalData, setGlobalData } = useVildmarkenState()
await callOnce(fetchGlobalData)

async function fetchGlobalData() {
  console.log('fetchGlobalData')
  try {
    const fetchers = {
      footer: () =>
        useSanityQuery(getFooter, {
          language: locale.value,
          server: true,
          lazy: true,
          maxAge: 600,
        }),
      navigation: () =>
        useContent(getNavigation, {
          cacheKey: 'global-navigation',
          server: true,
          lazy: true,
          maxAge: 600,
        }),
      settings: () =>
        useSanityQuery(getSettings, {
          language: locale.value,
          cacheKey: 'global-settings',
          server: true,
          lazy: true,
          maxAge: 600,
        }),
    }

    const responses = await Promise.allSettled(
      globalDataEndpoints.map((endpoint) => fetchers[endpoint]()),
    )

    const data = responses.reduce(
      (acc, res, idx) => {
        if (res.status !== 'fulfilled') return acc
        const key = camelize(globalDataEndpoints[idx])
        acc[key] = res.value.data
        return acc
      },
      {} as Record<string, any>,
    )

    setGlobalData(data)
  } catch (error) {
    throw createError({
      statusCode: 500,
      statusMessage: 'Could not get global data',
    })
  }
}
</script>

<template>
  <div vaul-drawer-wrapper class="relative bg-(--theme-bg) transition-colors duration-500">
    <NuxtLayout>
      <DevGrid />
      <!-- Header and Footer must not be wrapped in NuxtLayout 
       but rather in each page to ensure the transition doesn't break the layout 
       (header/footer being placed before page is one transitionning) -->
      <NuxtPage
        :transition="{
          name: 'page',
          mode: 'out-in',
          onBeforeLeave,
          onEnter,
        }"
      />
    </NuxtLayout>
  </div>
</template>

<style lang="postcss">
.page-leave-active,
.page-enter-active {
  transition:
    opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    filter 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-enter-from {
  opacity: 0;
  filter: blur(1rem);
}

.page-leave-to {
  opacity: 0;
  filter: blur(1rem);
}
</style>
